import { useEffect, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";

import { useToast } from "../../components/ui/toast";
import { cn } from "../../lib/utils";
import { listKnowledgeBases, pairsToText, setAgentKnowledge, textToPairs, type AgentKnowledgeConfig } from "./api";

const input = "w-full rounded-md border border-line bg-soft px-3 py-2 text-xs outline-none focus:border-brand";

/**
 * Agent-level, not part of the version draft: it saves on its own and applies to every
 * version of the agent. Calls that start after the save pick it up. Switching it off keeps
 * the chosen KB, so an agent can be paused without losing its setup.
 */
export function KnowledgeEditor({ agentId, saved }: { agentId: string | null; saved: AgentKnowledgeConfig | null }) {
  const toast = useToast();
  const { data: kbs = [] } = useQuery({ queryKey: ["knowledge-bases"], queryFn: listKnowledgeBases });
  const [enabled, setEnabled] = useState(saved?.enabled ?? false);
  const [kbId, setKbId] = useState(saved?.kb_id || "");
  const [topK, setTopK] = useState(saved?.top_k ?? 4);
  const [filtersText, setFiltersText] = useState(pairsToText(saved?.filters || {}));
  // The agent loads after mount; take its value once it arrives.
  const savedKey = JSON.stringify(saved);
  useEffect(() => {
    const value: AgentKnowledgeConfig | null = JSON.parse(savedKey);
    setEnabled(value?.enabled ?? false);
    setKbId(value?.kb_id || "");
    setTopK(value?.top_k ?? 4);
    setFiltersText(pairsToText(value?.filters || {}));
  }, [savedKey]);

  const save = useMutation({
    mutationFn: () =>
      setAgentKnowledge(agentId as string, kbId ? { enabled, kb_id: kbId, top_k: topK, filters: textToPairs(filtersText) } : null),
    onSuccess: (result) =>
      toast.success(result.knowledge?.enabled ? "Knowledge on" : "Knowledge off", "Applies to every version, from the next call."),
    onError: (error) => toast.danger("Could not save knowledge", (error as Error).message),
  });
  const published = kbs.filter((kb) => kb.active_version);

  return (
    <div>
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-sm font-bold text-ink">Knowledge</h2>
          <p className="mt-1 text-xs leading-5 text-ink-3">
            Lets the agent search a published knowledge base. Applies to every version of this agent. Manage documents in{" "}
            <Link to="/knowledge" className="text-brand">Knowledge</Link>.
          </p>
        </div>
        <button
          type="button"
          role="switch"
          aria-label="Enable knowledge base"
          aria-checked={enabled}
          disabled={!agentId}
          onClick={() => setEnabled(!enabled)}
          className={cn("relative h-5 w-9 shrink-0 rounded-full transition-colors disabled:opacity-50", enabled ? "bg-brand" : "bg-line")}
        >
          <span className={cn("absolute top-0.5 h-4 w-4 rounded-full bg-white transition-all", enabled ? "left-[18px]" : "left-0.5")} />
        </button>
      </div>
      {!agentId ? (
        <p className="mt-4 border-t border-line pt-4 text-xs text-ink-3">Save the agent first, then attach knowledge.</p>
      ) : !enabled ? (
        <div className="mt-4 flex items-center justify-between gap-4 border-t border-line pt-4">
          <p className="text-xs text-ink-3">Off: calls don't search any documents.</p>
          {saved?.enabled && <SaveButton pending={save.isPending} onClick={() => save.mutate()} />}
        </div>
      ) : (
        <div className="mt-4 grid gap-4 border-t border-line pt-4 md:grid-cols-2">
          <label className="text-[11px] font-bold text-ink-2">
            Knowledge base
            <select className={cn(input, "mt-1.5")} value={kbId} onChange={(e) => setKbId(e.target.value)}>
              <option value="">None</option>
              {published.map((kb) => <option key={kb.kb_id} value={kb.kb_id}>{kb.name}</option>)}
              {kbId && !published.some((kb) => kb.kb_id === kbId) && <option value={kbId}>{kbId}</option>}
            </select>
          </label>
          <label className="text-[11px] font-bold text-ink-2">
            Passages per search
            <input
              type="number"
              min={1}
              max={10}
              disabled={!kbId}
              className={cn(input, "mt-1.5")}
              value={topK}
              onChange={(e) => setTopK(Math.min(10, Math.max(1, Number(e.target.value) || 4)))}
            />
          </label>
          <label className="text-[11px] font-bold text-ink-2 md:col-span-2">
            Only search documents tagged for this call, one tag per line
            <textarea
              rows={2}
              disabled={!kbId}
              className={cn(input, "mt-1.5 font-mono")}
              value={filtersText}
              onChange={(e) => setFiltersText(e.target.value)}
              placeholder={"model: {{interest_model}}"}
            />
            <span className="mt-1 block font-normal text-ink-3">
              A call detail the call doesn't carry is ignored. Without this, an older manual's figures can be quoted.
            </span>
          </label>
          <div className="md:col-span-2">
            <SaveButton pending={save.isPending} disabled={!kbId} onClick={() => save.mutate()} />
          </div>
        </div>
      )}
    </div>
  );
}

function SaveButton({ pending, disabled = false, onClick }: { pending: boolean; disabled?: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      disabled={pending || disabled}
      onClick={onClick}
      className="rounded-md bg-brand px-3 py-2 text-xs font-bold text-white disabled:opacity-50"
    >
      Save knowledge
    </button>
  );
}
